/**
 * Network steps of the dashboard canvas.
 *
 * Each function is one step the canvas can retry on its own: plan a question,
 * save charts to the dashboard, run them. They throw `ApiError` unchanged so
 * the caller decides what the user sees; this module only logs.
 */
import {
  chartsApi,
  dashboardsApi,
  panelPlansApi,
  type PanelDraft,
  type PanelPlan,
} from "../../services/api";
import { createLogger, errorFields } from "../../services/clientLog";
import type { Dashboard, PanelResult } from "../../types";
import type { CanvasSource } from "./canvasModel";

export const logCanvas = createLogger("dashboard_canvas");

export async function planQuestion(
  question: string,
  source: CanvasSource,
): Promise<PanelPlan> {
  try {
    const plan = await panelPlansApi.plan({
      question: question.trim(),
      contextId: source.contextId,
      contextVersion: source.contextVersion || undefined,
    });
    logCanvas("info", "planned", {
      status: plan.status,
      panels: plan.panels.length,
      questionChars: question.length,
    });
    return plan;
  } catch (error) {
    logCanvas("error", "plan_failed", {
      ...errorFields(error),
      questionChars: question.length,
    });
    throw error;
  }
}

/** Creates the dashboard as a draft, holding the given charts in order. */
export async function createDashboard(
  name: string,
  source: CanvasSource,
  panels: PanelDraft[],
): Promise<Dashboard> {
  try {
    const dashboard = await dashboardsApi.create({
      name: name.trim(),
      contextId: source.contextId,
      contextVersion: source.contextVersion || null,
      panels,
    });
    logCanvas("info", "dashboard_created", {
      dashboardId: dashboard.id,
      panels: panels.length,
    });
    return dashboard;
  } catch (error) {
    logCanvas("error", "dashboard_create_failed", {
      ...errorFields(error),
      panels: panels.length,
    });
    throw error;
  }
}

/** Saves charts on their own, then attaches them to an existing dashboard. */
export async function attachPanels(
  dashboardId: string,
  panels: PanelDraft[],
): Promise<void> {
  try {
    const saved = await Promise.all(panels.map((draft) => chartsApi.save(draft)));
    await dashboardsApi.attachPanels(
      dashboardId,
      saved.map((panel) => panel.id),
    );
    logCanvas("info", "panels_attached", { dashboardId, panels: panels.length });
  } catch (error) {
    logCanvas("error", "panel_attach_failed", { ...errorFields(error), dashboardId });
    throw error;
  }
}

/** Runs the named charts and returns their results keyed by panel id. */
export async function runPanels(
  dashboardId: string,
  panelIds: string[],
): Promise<Map<string, PanelResult>> {
  try {
    const results = await dashboardsApi.refresh(dashboardId, panelIds);
    const failed = results.filter((result) => result.status === "error").length;
    logCanvas(failed ? "warn" : "info", "panels_ran", {
      dashboardId,
      panels: panelIds.length,
      failed,
    });
    return new Map(results.map((result) => [result.panelId, result]));
  } catch (error) {
    logCanvas("error", "panel_run_failed", { ...errorFields(error), dashboardId });
    throw error;
  }
}

/**
 * Removes a chart a box no longer shows. Best effort: a leftover chart is
 * untidy, not wrong, so a failure here is logged and never surfaced.
 */
export async function discardPanel(panelId: string): Promise<void> {
  try {
    await chartsApi.remove(panelId);
    logCanvas("info", "panel_discarded", { panelId });
  } catch (error) {
    logCanvas("warn", "panel_discard_failed", { ...errorFields(error), panelId });
  }
}
